import React, { useState, useEffect } from "react";
import { Switch } from "@headlessui/react";
import Input from "components/ui/Input";
import Select from "components/ui/Select";
import SelectVendors from "components/ui/selectors/SelectVendors";
import {
  useCreateSchemeMaster,
  useUpdateSchemeMaster,
  useOneSchemeMaster,
  useAllSchemeMasters,
} from "http-hooks/schemeMaster/useSchemeMaster";
import useAuth from "hooks/useAuth";

// Constants moved to top to prevent re-renders
const schemeTypeOptions = [
  { value: "", label: "Choose here" },
  { value: "R", label: "Retail" },
  { value: "W", label: "Wholesale" },
  { value: "C", label: "Case" },
];

const formTypeOptions = [
  { value: "", label: "Choose here" },
  { value: "S", label: "Sales" },
  { value: "P", label: "Purchases" },
];

export default function SetSchemeMaster({ id, onClose }) {
  const { agttp } = useAuth();
  const isEditMode = !!id;

  const [formData, setFormData] = useState({
    schmtp: "",
    sch_name: "",
    vendor: null,
    sch_date: "",
    sch_fm: "",
    sch_to: "",
    circlr_date: "",
    remark: "",
    schemefor: "",
    typ: "G", // Default to 'G'
    ag_status: 1,
    agttp: agttp || 0,
    schemecopy: null, // For copy scheme feature
  });

  const [vendorGroup, setVendorGroup] = useState(false);

  const { data: existingData, isLoading: isLoadingData } = useOneSchemeMaster(id);
  const { mutateAsync: mutateCreate, isPending: isCreating } = useCreateSchemeMaster();
  const { mutateAsync: mutateUpdate, isPending: isUpdating } = useUpdateSchemeMaster();
  
  // Fetch schemes by vendor for copy dropdown
  const { data: vendorSchemesData } = useAllSchemeMasters({
    vendor: formData.vendor?.ps_Id,
    agttp: agttp || 0,
    noPagination: true,
  });
  
  const vendorSchemes = vendorSchemesData?.data || [];

  useEffect(() => {
    if (existingData && isEditMode) {
      setFormData({
        schmtp: existingData.schmtp || "",
        sch_name: existingData.sch_name || "",
        vendor: existingData.vendorDetail
          ? existingData.vendorDetail
          : existingData.vendor
            ? { ps_Id: existingData.vendor, name: existingData.vendor }
            : null,
        sch_date: existingData.sch_date || "",
        sch_fm: existingData.sch_fm || "",
        sch_to: existingData.sch_to || "",
        circlr_date: existingData.circlr_date || "",
        remark: existingData.remark || "",
        schemefor: existingData.schemefor || "",
        typ: existingData.typ || "G",
        ag_status: existingData.ag_status,
        agttp: existingData.agttp || 0,
      });
      setVendorGroup(existingData.vendor_grp && existingData.vendor_grp.length > 0);
    }
  }, [existingData, isEditMode]);

  const handleSubmit = async () => {
    try {
      // Validate required fields
      if (!formData.schmtp) {
        return;
      }
      if (!formData.sch_name.trim()) {
        return;
      }
      if (!formData.vendor) {
        console.error("Vendor is required but not provided:", formData.vendor);
        return;
      }
      if (!formData.sch_date) {
        return;
      }
      if (!formData.sch_fm) {
        return;
      }
      if (!formData.sch_to) {
        return;
      }
      if (!formData.circlr_date) {
        return;
      }

      // Extract vendor value (SelectVendors uses ps_Id as the value field)
      const vendorValue = formData.vendor?.ps_Id || formData.vendor;
      
      if (!vendorValue) {
        console.error("Could not extract vendor value from:", formData.vendor);
        return;
      }

      const payload = {
        schmtp: formData.schmtp,
        sch_name: formData.sch_name.trim(),
        vendor: vendorValue,
        boolgrp: vendorGroup, // Send the checkbox state to backend
        sch_date: formData.sch_date,
        sch_fm: formData.sch_fm,
        sch_to: formData.sch_to,
        circlr_date: formData.circlr_date,
        remark: formData.remark,
        schemefor: formData.schemefor,
        typ: formData.typ,
        ag_status: formData.ag_status,
        agttp: formData.agttp,
      };

      // Add schemecopy if selected (only for create mode)
      if (!isEditMode && formData.schemecopy) {
        payload.schemecopy = formData.schemecopy.value;
      }

      if (isEditMode) {
        await mutateUpdate({ id, data: payload });
      } else {
        await mutateCreate(payload);
      }
      onClose();
    } catch (error) {
      console.error("Operation failed:", error);
    }
  };

  if (isLoadingData && isEditMode) {
    return (
      <div className="space-y-4">
        <div className="animate-pulse space-y-4">
          <div className="h-10 bg-gray-200 rounded"></div>
          <div className="h-10 bg-gray-200 rounded"></div>
          <div className="h-10 bg-gray-200 rounded"></div>
          <div className="h-10 bg-gray-200 rounded"></div>
        </div>
      </div>
    );
  }

  return (
    <div className="space-y-4">
      <h2 className="text-xl font-semibold mb-4">
        {isEditMode ? "Edit Scheme" : "Add Scheme"}
      </h2>

      {/* Show Form Type only for Trader (agttp === 1) */}
      {agttp === 1 && (
        <Select
          label="Scheme Type *"
          options={formTypeOptions}
          value={formTypeOptions.find((opt) => opt.value === formData.typ)}
          onChange={(option) =>
            setFormData({ ...formData, typ: option ? option.value : "G" })
          }
          placeholder="Choose here"
          isClearable={false}
        />
      )}

      <Select
        label="Type *"
        options={schemeTypeOptions}
        value={schemeTypeOptions.find((opt) => opt.value === formData.schmtp)}
        onChange={(option) =>
          setFormData({ ...formData, schmtp: option ? option.value : "" })
        }
        placeholder="Choose here"
        isClearable={false}
      />

      <SelectVendors
        label="Vendor *"
        value={formData.vendor}
        onChange={(value) => {
          setFormData({ ...formData, vendor: value, schemecopy: null });
        }}
        isClearable={false}
      />

      <Input
        type="checkbox"
        label="Group"
        value={vendorGroup}
        onChange={(e) => setVendorGroup(e.target.checked)}
      />

      {/* Copy Scheme From - Only show in create mode and when vendor is selected */}
      {!isEditMode && formData.vendor && vendorSchemes.length > 0 && (
        <Select
          label="Copy Scheme From"
          options={vendorSchemes.map((scheme) => ({
            value: scheme.schemeid,
            label: scheme.sch_name,
          }))}
          value={formData.schemecopy}
          onChange={(option) =>
            setFormData({ ...formData, schemecopy: option })
          }
          placeholder="Select scheme to copy"
          isClearable={true}
        />
      )}

      <Input
        label="Name *"
        type="text"
        value={formData.sch_name}
        onChange={(e) => setFormData({ ...formData, sch_name: e.target.value })}
        placeholder="Enter Scheme Name"
        maxLength={40}
        required
      />

      <Input
        label="Announced Date *"
        type="date"
        value={formData.sch_date}
        onChange={(e) => setFormData({ ...formData, sch_date: e.target.value })}
        required
      />

      <Input
        label="Circular Date *"
        type="date"
        value={formData.circlr_date}
        onChange={(e) =>
          setFormData({ ...formData, circlr_date: e.target.value })
        }
        required
      />

      <Input
        label="From Date *"
        type="date"
        value={formData.sch_fm}
        onChange={(e) => setFormData({ ...formData, sch_fm: e.target.value })}
        required
      />

      <Input
        label="To Date *"
        type="date"
        value={formData.sch_to}
        onChange={(e) => setFormData({ ...formData, sch_to: e.target.value })}
        min={formData.sch_fm}
        required
      />

      <Input
        label="Remark"
        type="textarea"
        value={formData.remark}
        onChange={(e) => setFormData({ ...formData, remark: e.target.value })}
        placeholder="Remark"
        maxLength={50}
        rows={3}
      />

      {/* Status Switch */}
      <div className="flex items-center justify-between">
        <span className="flex flex-grow flex-col">
          <span className="text-sm font-medium leading-6 text-gray-900">
            Status
          </span>
          <span className="text-sm text-gray-500">
            {formData.ag_status === 1 ? "Active" : "Inactive"}
          </span>
        </span>
        <Switch
          checked={formData.ag_status === 1}
          onChange={(checked) =>
            setFormData({ ...formData, ag_status: checked ? 1 : 0 })
          }
          className={`${
            formData.ag_status === 1 ? "bg-indigo-600" : "bg-gray-300"
          } relative inline-flex h-6 w-11 items-center rounded-full transition-colors focus:outline-none focus:ring-2 focus:ring-indigo-600 focus:ring-offset-2`}
        >
          <span
            className={`${
              formData.ag_status === 1 ? "translate-x-6" : "translate-x-1"
            } inline-block h-4 w-4 transform rounded-full bg-white transition-transform`}
          />
        </Switch>
      </div>

      <div className="flex justify-end space-x-3 mt-6">
        <button
          type="button"
          onClick={onClose}
          className="text-sm font-semibold rounded-md px-3 py-2 hover:bg-gray-100 text-gray-900"
          disabled={isCreating || isUpdating}
        >
          Cancel
        </button>
        <button
          type="button"
          onClick={handleSubmit}
          disabled={isCreating || isUpdating}
          className="rounded-md bg-gray-700 px-3 py-2 text-sm font-semibold text-white shadow-sm hover:bg-gray-500 focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-gray-700 disabled:opacity-50 disabled:cursor-not-allowed"
        >
          {isCreating || isUpdating ? "Saving..." : "Save"}
        </button>
      </div>
    </div>
  );
}
